import React, { useState, useEffect } from 'react';
import {
    TextField,
    BooleanField,
    DateField,
    EditButton,
    DeleteButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    required,
    useNotify,
    useInput,
    useRecordContext,
    Confirm,
    useRefresh
} from 'react-admin';
import { Button, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { CloudUpload as UploadIcon, PermMedia, ContentCopy as CopyIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { httpClient, publicBase } from './dataProvider';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';

// Custom file upload input component
const FileUploadInput = ({ source, label }) => {
    const notify = useNotify();
    const { field } = useInput({ source });
    const [uploading, setUploading] = useState(false);
    const [fileName, setFileName] = useState('');

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            // Use the same auth header pattern as existing resources
            const response = await fetch(`${publicBase}/media_files/upload_file`, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-User-Id': localStorage.getItem('userId') || '91a37b17-10ce-41c2-860d-890605802763'
                }
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            field.onChange(data.url); // Set the URL form field value
            setFileName(file.name);
            notify('File uploaded successfully', { type: 'success' });
        } catch (error) {
            console.error(error);
            notify('Error uploading file', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Button
                    component="label"
                    variant="contained"
                    startIcon={<UploadIcon />}
                    disabled={uploading}
                >
                    {uploading ? "Uploading..." : "Choose File"}
                    <input type="file" hidden onChange={handleFileChange} accept="video/*,audio/*,image/*,application/pdf" />
                </Button>
                {fileName && <span>{fileName}</span>}
            </div>
            {field.value && (
                <div style={{ marginTop: 10, fontSize: '0.8em', color: 'gray' }}>
                    Uploaded Path: {field.value}
                </div>
            )}
        </div>
    );
};

const CopyInput = ({ source, label }) => {
    const notify = useNotify();
    const record = useRecordContext();

    if (!record) return null;

    const handleCopy = () => {
        const url = record[source];
        if (!url) return;

        let copyText = url;
        const extension = url.split('.').pop().toLowerCase();

        if (['mp4', 'webm', 'ogg', 'mov'].includes(extension)) {
            copyText = `<video src="${url}" controls width="100%"></video>`;
        } else if (['mp3', 'wav'].includes(extension)) {
            copyText = `<audio src="${url}" controls style="width: 100%"></audio>`;
        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
            copyText = `<img src="${url}" width="100%" />`;
        } else if (extension === 'pdf') {
            copyText = `<embed src="${url}" type="application/pdf" width="100%" height="800px" />`;
        }

        navigator.clipboard.writeText(copyText);
        notify('Copied embed code to clipboard', { type: 'success' });
    };

    return (
        <TextInput
            source={source}
            label={label}
            fullWidth
            readOnly
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton onClick={handleCopy} edge="end" title="Copy to clipboard">
                            <CopyIcon />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );
};



const DeleteMediaFileButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/media_files/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Media file deleted successfully', { type: 'success' });
            refresh();
            // Force reload of the list if refresh doesn't trigger a re-mount/re-fetch in this custom setup
            window.location.reload();
        } catch (error) {
            console.error('Error deleting media file:', error);
            notify('Failed to delete media file', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                label="Delete"
                color="error"
                startIcon={<DeleteIcon />}
            />
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Media File"
                content="Are you sure you want to delete this file? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

export const MediaFileList = () => {
    const [mediaFiles, setMediaFiles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/media_files?end=1000`; // Fetch all for client-side sort
        httpClient(url)
            .then(({ json }) => {
                setMediaFiles(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching media files:', error);
                setLoading(false);
            });
    }, []);

    const columns = [
        { key: 'name', header: 'Name' },
        { key: 'description', header: 'Description' },
        { key: 'url', header: 'GCS Path' },
        {
            key: 'active',
            header: 'Active',
            render: (record) => record.active ? "Yes" : "No"
        },
        {
            key: 'created_at',
            header: 'Created At',
            render: (record) => new Date(record.created_at).toLocaleString()
        },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="media_files" />
                    <DeleteMediaFileButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Media Manager</h2>
                <Link
                    to="/media_files/create"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Create
                </Link>
            </div>
            <SortableTable data={mediaFiles} columns={columns} />
        </div>
    );
};

export const MediaFileEdit = () => (
    <Edit title="Edit Media File">
        <SimpleForm>
            <TextInput source="name" validate={required()} fullWidth />
            <TextInput source="description" multiline fullWidth />
            <CopyInput source="url" label="GCS Path (Read Only)" />
            <BooleanInput source="active" />
        </SimpleForm>
    </Edit>
);

export const MediaFileCreate = () => (
    <Create title="Create Media File">
        <SimpleForm>
            <TextInput source="name" validate={required()} fullWidth />
            <TextInput source="description" multiline fullWidth />
            <FileUploadInput source="url" />
            {/* Hidden input to ensure the source is registered in the form even if FileUploadInput manages it implicitly via useInput,
                though useInput connects to form state directly so this might be redundant but safe */}
            <TextInput source="url" style={{ display: 'none' }} />

            <BooleanInput source="active" defaultValue={true} />
        </SimpleForm>
    </Create>
);

// Icon for the resource
export const MediaIcon = PermMedia;
